import React from 'react'
import { graphql } from 'gatsby'
import Helmet from 'react-helmet'
import { useLocation } from '@reach/router'
import Layout from '../components/layout'
import WpAuthor from '../components/WpAuthor'
import { mapLocaleData } from '../utils'

const AuthorTemplate = ({ data }) => {
  const siteTitle = data.site.siteMetadata.title
  const navBarData = data.contentfulNavigationBar
  const footerData = data.contentfulFooterV2
  const authorData = data.wpAuthor
  const authorPosts = data.allWpPost.edges
  const defaultdata = data.wp.generalSettings
  const siteLanguages = data.site.siteMetadata.languages
  const latestBlogPost = data.latestPost.edges[0].node
  const location = useLocation()

  return (
    <Layout
      latestBlogPost={latestBlogPost}
      navBarData={navBarData}
      footerData={footerData}
      siteLanguages={siteLanguages}
      location={location}
    >
      <div style={{ background: '#fff' }}>
        <Helmet title={siteTitle} />
        <WpAuthor
          data={authorData}
          defaultdata={defaultdata}
          location={location}
          authorPosts={authorPosts}
        />
      </div>
    </Layout>
  )
}

export default AuthorTemplate

export const pageQuery = graphql`
  fragment authorNode on WpAuthor {
    id
    title
    locale {
      locale
      id
    }
    slug
    authorDetails {
      detailedBio
      jobTitle
      socialLinks {
        linkedinUrl
        twitterUrl
      }
      bannerImage {
        localFile {
          childImageSharp {
            fluid(maxWidth: 1440, maxHeight: 500, cropFocus: CENTER) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
          publicURL
        }
        altText
      }
      profilePicture {
        localFile {
          childImageSharp {
            fluid(maxWidth: 168, maxHeight: 168, cropFocus: CENTER) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
          publicURL
        }
        altText
      }
    }
  }

  query ($id: ID!, $authorId: String!) {
    site {
      siteMetadata {
        title
        languages {
          defaultLangKey
          langs
        }
      }
    }

    latestPost: allWpPost(
      sort: { fields: date, order: DESC }
      limit: 1
      filter: { locale: { locale: { eq: "en_US" } }, status: { eq: "publish" } }
    ) {
      edges {
        node {
          ...LatestPost
        }
      }
    }

    contentfulNavigationBar(node_locale: { eq: "en-US" }) {
      title
      logo {
        fixed {
          src
        }
      }
      superNavLinks {
        label
        order
        defaultUrl
        hide
      }
      links {
        label
        hoverGradient
        inverted
        order
        defaultUrl
        hide
        groupHeader
        groupHeaderUrl
        groupHeaderImage {
          file {
            fileName
            url
          }
          description
          gatsbyImageData(layout: CONSTRAINED, width: 30, height: 30)
        }
        groupId
        childLinks {
          ctaText
          url
          label
          subtitle
          type
          order
          newProductLink
          hide
          image {
            fixed {
              src
            }
            file {
              fileName
              url
            }
          }
        }
      }
    }

    wp {
      generalSettings {
        authorBannerDefault {
          localFile {
            childImageSharp {
              fluid(maxWidth: 1440, maxHeight: 500, cropFocus: CENTER) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
            featured: childImageSharp {
              fluid(maxWidth: 864, maxHeight: 438, cropFocus: CENTER) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
            previewCardImage: childImageSharp {
              gatsbyImageData(width: 428, height: 217, placeholder: BLURRED)
            }
            publicURL
          }
          altText
        }
        authorProfileDefault {
          localFile {
            childImageSharp {
              fluid(maxWidth: 168, maxHeight: 168, cropFocus: CENTER) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
            publicURL
          }
          altText
        }
      }
    }

    allWpPost(
      filter: { authorId: { eq: $id }, locale: { locale: { eq: "en_US" } } }
    ) {
      edges {
        node {
          ...previewCardQuery
        }
      }
    }

    wpAuthor(id: { eq: $authorId }, locale: { locale: { eq: "en_US" } }) {
      ...authorNode
    }

    contentfulFooterV2(node_locale: { eq: "en-US" }) {
      logo {
        fixed(width: 64) {
          ...GatsbyContentfulFixed_withWebp
        }
      }
      logoAttr {
        width
        height
        maxWidth
        maxHeight
      }
      nyseLink
      nyseLogo {
        fixed(width: 75) {
          ...GatsbyContentfulFixed_withWebp
        }
        file {
          url
        }
      }
      copyrightText
      privacyPolicyText
      privacyPolicyUrl
      siteTermsText
      siteTermsUrl
      basedInText
      sections {
        url
        label
        sublinks {
          label
          url
        }
      }
      mobileButtons {
        label
        url
      }
    }
  }
`
